(function () {
    const CLIENT_STORAGE_KEY = 'ff_mb_client';
    const REGION_STORAGE_KEY = 'ff_user_region';
    const EVENT = 'ff:mb-client';
    const SCHEMA_VERSION = 2;
    // Stale-while-revalidate window: cached data paints instantly, then a
    // background refetch runs if it's older than this. In-app mutations
    // (patch()/refresh(true)) bypass this entirely and update immediately.
    const TTL_MS = 10 * 60 * 1000;

    const REGION_TO_SITE = {
        'Los Angeles': { code: 'LA', siteId: '5729211' },
        'Vancouver':   { code: 'BC', siteId: '493689' },
        'Toronto':     { code: 'ON', siteId: '5726529' },
    };

    let inflight = null;

    function getCustomerEmail() {
        return document.querySelector('meta[name="ff:customer-email"]')?.content ?? null;
    }

    function getSiteId() {
        const raw = localStorage.getItem(REGION_STORAGE_KEY);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed?.siteId) return parsed.siteId;
            if (parsed?.region) return REGION_TO_SITE[parsed.region]?.siteId ?? null;
            return null;
        } catch {
            return REGION_TO_SITE[raw]?.siteId ?? null;
        }
    }

    // Raw parsed entry (any schema version), or null if absent/unparsable.
    function readRaw() {
        try {
            const raw = localStorage.getItem(CLIENT_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    }

    // Valid v2 entry for the given email, or null (discards stale schema/email).
    function validEntryFor(email) {
        const entry = readRaw();
        if (!entry || entry.v !== SCHEMA_VERSION || entry.shopifyEmail !== email) return null;
        return entry;
    }

    // The public shape consumers see: the flat client, or null (noClient sentinel
    // collapses to null so existing readers never need to know about it).
    function publicValue(entry) {
        return (!entry || entry.noClient) ? null : entry;
    }

    function dispatch(entry) {
        const value = publicValue(entry);
        document.documentElement.setAttribute('data-mb-client', value ? 'present' : 'none');
        document.dispatchEvent(new CustomEvent(EVENT, { detail: value }));
    }

    function save(entry) {
        localStorage.setItem(CLIENT_STORAGE_KEY, JSON.stringify(entry));
    }

    function clear() {
        localStorage.removeItem(CLIENT_STORAGE_KEY);
        dispatch(null);
    }

    // Ignores fetchedAt so a background refetch that returns identical data
    // doesn't trigger a redundant re-render.
    function entriesEqual(a, b) {
        const strip = (e) => { const { fetchedAt, ...rest } = e; return rest; };
        return JSON.stringify(strip(a)) === JSON.stringify(strip(b));
    }

    async function doFetch(email, siteId, stale) {
        try {
            const url = new URL(Theme.routes.booking_proxy_path + '/customer/client', location.origin);
            url.searchParams.set('siteId', siteId);
            const res = await fetch(url.toString(), {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            if (!res.ok) throw new Error('bad response');

            const data = await res.json();
            const client = data?.client ?? null;
            const entry = client
                ? { v: SCHEMA_VERSION, shopifyEmail: email, fetchedAt: Date.now(), ...client }
                : { v: SCHEMA_VERSION, shopifyEmail: email, fetchedAt: Date.now(), noClient: true };

            save(entry);
            if (!stale || !entriesEqual(stale, entry)) dispatch(entry);
            return publicValue(entry);
        } catch {
            // Transient failure: keep whatever stale data exists rather than
            // conflating "couldn't reach the server" with "no client". Only
            // unblock listeners on a cold load (no cache to fall back to).
            if (!stale) dispatch(null);
            return publicValue(stale);
        }
    }

    // Public: fetch fresh data. force=true always hits the network (coalesced
    // with any in-flight call); force=false is a no-op while the cache is fresh.
    function refresh(force) {
        if (inflight) return inflight;

        const email = getCustomerEmail();
        if (!email) {
            clear();
            return Promise.resolve(null);
        }

        const stale = validEntryFor(email);
        if (!force && stale && Date.now() - stale.fetchedAt < TTL_MS) {
            return Promise.resolve(publicValue(stale));
        }

        const siteId = getSiteId() || (stale && stale.siteId);
        if (!siteId) {
            document.addEventListener('ff:region', () => refresh(force), { once: true });
            return Promise.resolve(publicValue(stale));
        }

        inflight = doFetch(email, siteId, stale).finally(() => { inflight = null; });
        return inflight;
    }

    // Public: merge fields into the current valid entry and always dispatch.
    // Falls back to a full refresh when there's no valid entry to merge into,
    // rather than writing a bare partial entry with no shopifyEmail/fetchedAt.
    function patch(fields) {
        const email = getCustomerEmail();
        if (!email) return;

        const entry = validEntryFor(email);
        if (!entry || entry.noClient) {
            refresh(true);
            return;
        }

        const next = { ...entry, ...fields };
        delete next.noClient;
        save(next);
        dispatch(next);
    }

    function sync() {
        const email = getCustomerEmail();

        if (!email) {
            clear();
            return;
        }

        const raw = readRaw();
        if (raw && (raw.v !== SCHEMA_VERSION || raw.shopifyEmail !== email)) {
            localStorage.removeItem(CLIENT_STORAGE_KEY);
        }

        const entry = validEntryFor(email);
        if (entry) {
            dispatch(entry);
            if (Date.now() - (entry.fetchedAt || 0) >= TTL_MS) refresh(false);
        } else {
            refresh(true);
        }
    }

    window.ffMbClient = {
        EVENT,
        get: () => publicValue(validEntryFor(getCustomerEmail())),
        has: () => document.documentElement.getAttribute('data-mb-client') === 'present',
        refresh,
        patch,
        clear,
    };

    // Same-tab writes don't fire 'storage' — this covers other tabs so they
    // repaint when one tab refreshes, patches, or clears the cache.
    window.addEventListener('storage', (e) => {
        if (e.key === CLIENT_STORAGE_KEY) dispatch(validEntryFor(getCustomerEmail()));
    });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', sync, { once: true });
    } else {
        sync();
    }
})();
